import React, { useState, useEffect } from 'react';
import { FileText, PlusCircle, Search, Download, CheckCircle, AlertCircle, Loader2, Send, Printer, FileSpreadsheet, Calendar } from 'lucide-react';
import api from '../services/api';

const NotasFiscaisScreen = () => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
        return { start, end };
    }); // 🆕

    // Emission Modal State
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState({
        payerId: '', amount: '', description: '',
        cnae: '', itemLc116: '', codigoNbs: '', issRetido: false, responsavelRetencao: 2, obraCode: '', art: '',
        naturezaOperacao: '1', dataCompetencia: new Date().toISOString().split('T')[0], municipioIncidencia: '', estadoIncidencia: '',
        saveAsDefault: false
    });
    const [companies, setCompanies] = useState([]);
    const [clientSearch, setClientSearch] = useState(''); // 🆕
    const [showClientDropdown, setShowClientDropdown] = useState(false); // 🆕
    const [emitting, setEmitting] = useState(false);
    const [configDefaults, setConfigDefaults] = useState({}); // 🆕

    useEffect(() => {
        loadNotes();
        loadCompanies();
        loadConfig();
    }, [dateRange]);

    const loadConfig = async () => {
        setLoading(true);
        try {
            const res = await api.get('/integrations'); // Corrigido de /settings para /integrations
            if (res.data && res.data.nfse) {
                setConfigDefaults(res.data.nfse);
                // Pre-fill form IF empty
                setForm(prev => ({
                    ...prev,
                    cnae: res.data.nfse.cnae || '',
                    itemLc116: res.data.nfse.itemLc116 || res.data.nfse.defaultServiceCode || '',
                    codigoNbs: res.data.nfse.codigoNbs || '',
                    issRetido: res.data.nfse.issRetido || false,
                    responsavelRetencao: res.data.nfse.responsavelRetencao || 2,
                    obraCode: res.data.nfse.obraCode || '',
                    art: res.data.nfse.art || '',
                    naturezaOperacao: res.data.nfse.naturezaOperacao || '1',
                    municipioIncidencia: res.data.nfse.municipioIncidencia || '',
                    estadoIncidencia: res.data.nfse.estadoIncidencia || '',
                    dataCompetencia: new Date().toISOString().split('T')[0]
                }));
            }
        } catch (e) {
            console.error("Erro ao carregar configurações fiscais", e);
        } finally {
            setLoading(false);
        }
    };

    const loadNotes = async () => {
        setLoading(true);
        try {
            const params = {};
            if (dateRange.start && dateRange.end) {
                params.startDate = dateRange.start;
                params.endDate = dateRange.end;
            }
            const res = await api.get('/nfse', { params });
            setNotes(res.data);
        } catch (error) { console.error(error); }
        finally { setLoading(false); }
    };

    const loadCompanies = async () => {
        try {
            const res = await api.get('/external-companies?limit=1000');
            if (res.data && Array.isArray(res.data.data)) {
                setCompanies(res.data.data);
            } else if (Array.isArray(res.data)) {
                setCompanies(res.data);
            } else {
                setCompanies([]);
            }
        }
        catch (e) { }
    };

    const handleEmit = async () => {
        if (!form.payerId || !form.amount || !form.description) return alert("Preencha todos os campos.");
        setEmitting(true);
        try {
            await api.post('/nfse', form);
            alert("NFS-e enviada para processamento!");
            setModalOpen(false);
            // Reset form but keep defaults? No, full reset and re-apply defaults is safer or just reset main fields.
            // Let's reset main fields and re-apply config defaults manually or just reload.
            setForm({
                payerId: '', amount: '', description: '',
                cnae: configDefaults.cnae || '',
                itemLc116: configDefaults.itemLc116 || '',
                codigoNbs: configDefaults.codigoNbs || '',
                issRetido: configDefaults.issRetido || false,
                responsavelRetencao: configDefaults.responsavelRetencao || 2,
                obraCode: configDefaults.obraCode || '',
                art: configDefaults.art || '',
                naturezaOperacao: configDefaults.naturezaOperacao || '1',
                municipioIncidencia: configDefaults.municipioIncidencia || '',
                estadoIncidencia: configDefaults.estadoIncidencia || '',
                dataCompetencia: new Date().toISOString().split('T')[0],
                saveAsDefault: false
            });
            if (form.saveAsDefault) loadConfig(); // Reload defaults if changed
            loadNotes();
        } catch (error) {
            alert("Erro ao emitir: " + (error.response?.data?.error || error.message));
        } finally {
            setEmitting(false);
        }
    };

    const handleSaveAsModel = async () => {
        const name = prompt("Nome para salvar este modelo (ex: Consultoria):");
        if (!name) return;

        try {
            // Criar o objeto do novo modelo
            const newModel = {
                name,
                cnae: form.cnae,
                itemLc116: form.itemLc116,
                codigoNbs: form.codigoNbs,
                issRetido: form.issRetido,
                responsavelRetencao: form.responsavelRetencao,
                obraCode: form.obraCode,
                art: form.art,
                naturezaOperacao: form.naturezaOperacao,
                municipioIncidencia: form.municipioIncidencia,
                estadoIncidencia: form.estadoIncidencia
            };

            // Buscar config atual completa para não perder dados
            const res = await api.get('/integrations');
            const currentConfig = res.data;

            const updatedNfse = {
                ...currentConfig.nfse,
                models: [...(currentConfig.nfse?.models || []), newModel]
            };

            await api.put('/integrations', { ...currentConfig, nfse: updatedNfse });
            alert(`Modelo "${name}" salvo com sucesso!`);
            loadConfig(); // Recarregar para aparecer na lista
        } catch (error) {
            alert("Erro ao salvar modelo: " + error.message);
        }
    };

    const handleModelSelect = (e) => {
        const modelName = e.target.value;
        if (!modelName) return;
        const model = configDefaults.models.find(m => m.name === modelName);
        if (model) {
            setForm(prev => ({
                ...prev,
                cnae: model.cnae || '',
                itemLc116: model.itemLc116 || '',
                codigoNbs: model.codigoNbs || '',
                issRetido: model.issRetido || false,
                responsavelRetencao: model.responsavelRetencao || 2,
                obraCode: model.obraCode || '',
                art: model.art || '',
                naturezaOperacao: model.naturezaOperacao || '1',
                municipioIncidencia: model.municipioIncidencia || '',
                estadoIncidencia: model.estadoIncidencia || ''
            }));
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'authorized': return <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold flex items-center"><CheckCircle className="w-3 h-3 mr-1" /> Autorizada</span>;
            case 'processing': return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-bold flex items-center"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Processando</span>;
            case 'error': return <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold flex items-center"><AlertCircle className="w-3 h-3 mr-1" /> Erro</span>;
            case 'canceled': return <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-bold flex items-center"><FileText className="w-3 h-3 mr-1" /> Cancelada</span>;
            default: return status;
        }
    };

    const handlePrintList = () => {
        const printWindow = window.open('', '_blank', 'width=900,height=600');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Relatório de Notas Fiscais</title>
                    <style>
                        body { font-family: sans-serif; padding: 20px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
                        th { background-color: #f2f2f2; }
                        .header { margin-bottom: 20px; border-bottom: 2px solid #ccc; padding-bottom: 10px; }
                        h1 { margin: 0; font-size: 18px; }
                        .summary { margin-top: 20px; font-weight: bold; text-align: right; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>Relatório de Notas Fiscais (NFS-e)</h1>
                        <p>Período: ${dateRange.start ? new Date(dateRange.start).toLocaleDateString() : 'Início'} a ${dateRange.end ? new Date(dateRange.end).toLocaleDateString() : 'Fim'}</p>
                        <p>Gerado em: ${new Date().toLocaleString()}</p>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Número</th>
                                <th>Tomador</th>
                                <th>Emissão</th>
                                <th>Status</th>
                                <th style="text-align: right;">Valor</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${notes.map(note => `
                                <tr>
                                    <td>${note.number || 'Pendente'}</td>
                                    <td>${note.payerId?.name || 'Desconhecido'}</td>
                                    <td>${new Date(note.issueDate).toLocaleDateString()}</td>
                                    <td>${note.status === 'authorized' ? 'AUTORIZADA' : (note.status === 'canceled' ? 'CANCELADA' : 'PROCESSANDO')}</td>
                                    <td style="text-align: right;">R$ ${note.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <div class="summary">
                        Total: R$ ${notes.reduce((acc, curr) => acc + (curr.amount || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </div>
                    <script>window.onload = function() { window.print(); }</script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleExportExcel = () => {
        const headers = ["Número", "Tomador", "Emissão", "Status", "Valor"];
        const rows = notes.map(note => [
            note.number || 'Pendente',
            note.payerId?.name || 'Desconhecido',
            new Date(note.issueDate).toLocaleDateString(),
            note.status,
            (note.amount || 0).toFixed(2).replace('.', ',')
        ]);

        const csvContent = [
            headers.join(";"),
            ...rows.map(e => e.join(";"))
        ].join("\n");

        const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `nfse_relatorio_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };



    return (
        <div className="flex-1 flex flex-col h-full bg-gray-50 p-6 overflow-hidden">
            <div className="flex flex-col gap-4 mb-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center"><FileText className="mr-2 text-blue-600" /> Notas Fiscais (NFS-e)</h1>
                        <p className="text-sm text-gray-500">Gerenciar e emitir notas fiscais de serviço</p>
                    </div>
                    <button onClick={() => setModalOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold flex items-center shadow-lg transition-all">
                        <PlusCircle className="mr-2 w-5 h-5" /> Emitir NFS-e
                    </button>
                </div>

                <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-3 rounded-lg shadow-sm border">
                    <div className="flex items-center gap-2">
                        <Calendar className="text-gray-400 w-5 h-5" />
                        <span className="text-sm font-bold text-gray-600">Período:</span>
                        <input type="date" className="border rounded p-1 text-sm text-gray-600 outline-none focus:border-blue-500 transition-all" value={dateRange.start} onChange={e => setDateRange({ ...dateRange, start: e.target.value })} />
                        <span className="text-gray-400">-</span>
                        <input type="date" className="border rounded p-1 text-sm text-gray-600 outline-none focus:border-blue-500 transition-all" value={dateRange.end} onChange={e => setDateRange({ ...dateRange, end: e.target.value })} />
                    </div>

                    <div className="flex gap-2">
                        <button onClick={handlePrintList} className="bg-white border hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg font-bold flex items-center transition-colors text-sm shadow-sm" title="Imprimir Lista">
                            <Printer className="w-4 h-4 mr-2" /> Imprimir
                        </button>
                        <button onClick={handleExportExcel} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-bold flex items-center transition-colors text-sm shadow-sm" title="Exportar CSV">
                            <FileSpreadsheet className="w-4 h-4 mr-2" /> Exportar
                        </button>
                    </div>
                </div>
            </div>




            {/* List */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden flex-1 overflow-y-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 text-gray-600 uppercase font-bold border-b sticky top-0">
                        <tr>
                            <th className="px-6 py-3">Número</th>
                            <th className="px-6 py-3">Tomador</th>
                            <th className="px-6 py-3">Emissão</th>
                            <th className="px-6 py-3 text-right">Valor</th>
                            <th className="px-6 py-3 text-center">Status</th>
                            <th className="px-6 py-3 text-center">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {notes.length === 0 ? <tr><td colSpan="6" className="p-8 text-center text-gray-500">Nenhuma nota emitida.</td></tr> : notes.map(note => (
                            <tr key={note._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 font-mono">{note.number || 'Pendente'}</td>
                                <td className="px-6 py-4 font-medium">{note.payerId?.name || 'Desconhecido'}</td>
                                <td className="px-6 py-4 text-gray-500">{new Date(note.issueDate).toLocaleDateString()}</td>
                                <td className="px-6 py-4 text-right font-bold text-gray-800">R$ {note.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                <td className="px-6 py-4 flex justify-center">{getStatusBadge(note.status)}</td>
                                <td className="px-6 py-4 text-center">
                                    {note.pdfUrl ? (
                                        <a href={note.pdfUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center justify-center font-bold text-xs"><Download className="w-3 h-3 mr-1" /> PDF</a>
                                    ) : (
                                        <span className="text-gray-400 text-xs">-</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal de Emissão - LAYOUT AMPLIADO */}
            {
                modalOpen && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl overflow-hidden animate-fadeIn flex flex-col max-h-[90vh]">
                            <div className="bg-gray-100 px-6 py-4 border-b flex justify-between items-center shrink-0">
                                <h3 className="font-bold text-gray-800 text-lg flex items-center"><FileText className="mr-2 text-blue-600" /> Nova Nota Fiscal (NFS-e)</h3>
                                <button onClick={() => setModalOpen(false)} className="text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-full p-1 transition-colors">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>

                            <div className="p-6 space-y-4 overflow-y-auto custom-scrollbar flex-1">
                                {/* Seletor de Modelos + Reload */}
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-4">
                                    <label className="block text-xs font-bold text-blue-700 mb-1 uppercase tracking-wider">Carregar Modelo Salvo</label>
                                    <div className="flex gap-2">
                                        <select className="flex-1 p-2 text-sm border-blue-200 rounded focus:ring-2 focus:ring-blue-500 outline-none" onChange={handleModelSelect} defaultValue="">
                                            <option value="" disabled>Selecione um modelo para preencher automaticamente...</option>
                                            {(configDefaults.models || []).map((m, i) => (
                                                <option key={i} value={m.name}>{m.name}</option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={loadConfig}
                                            className="bg-blue-100 text-blue-700 hover:bg-blue-200 px-3 py-2 rounded border border-blue-200 transition-colors"
                                            title="Atualizar lista de modelos"
                                        >
                                            <Loader2 className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                        </button>
                                    </div>
                                </div>

                                {/* GRID PRINCIPAL */}
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">

                                    {/* LINHA 1: Dados Fiscais Básicos */}
                                    <div className="md:col-span-3">
                                        <label className="block text-xs font-bold text-gray-500 mb-1">Natureza Operação</label>
                                        <select className="w-full p-2 border rounded text-sm bg-white focus:ring-2 focus:ring-blue-500" value={form.naturezaOperacao} onChange={e => setForm({ ...form, naturezaOperacao: e.target.value })}>
                                            <option value="1">1 - Tributação no município</option>
                                            <option value="2">2 - Tributação fora do município</option>
                                            <option value="3">3 - Isenção</option>
                                            <option value="4">4 - Imune</option>
                                            <option value="5">5 - Exigibilidade Suspensa</option>
                                        </select>
                                    </div>
                                    <div className="md:col-span-3">
                                        <label className="block text-xs font-bold text-gray-500 mb-1">Data Competência</label>
                                        <input type="date" className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-blue-500" value={form.dataCompetencia} onChange={e => setForm({ ...form, dataCompetencia: e.target.value })} />
                                    </div>
                                    <div className="md:col-span-3">
                                        <label className="block text-xs font-bold text-gray-500 mb-1">Município Incidência</label>
                                        <input type="text" className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-blue-500" placeholder="Ex: São Paulo" value={form.municipioIncidencia} onChange={e => setForm({ ...form, municipioIncidencia: e.target.value })} />
                                    </div>
                                    <div className="md:col-span-3">
                                        <label className="block text-xs font-bold text-gray-500 mb-1">UF Incidência</label>
                                        <input type="text" className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-blue-500 uppercase" placeholder="Ex: SP" maxLength={2} value={form.estadoIncidencia} onChange={e => setForm({ ...form, estadoIncidencia: e.target.value.toUpperCase() })} />
                                    </div>

                                    {/* LINHA 2: Cliente e Valor */}
                                    <div className="md:col-span-8 relative">
                                        <label className="block text-sm font-bold text-gray-700 mb-1">Tomador (Cliente)</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Search className="h-4 w-4 text-gray-400" />
                                            </div>
                                            <input
                                                type="text"
                                                className="w-full pl-9 p-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50 text-gray-900"
                                                placeholder="Busque por Nome ou CNPJ..."
                                                value={clientSearch}
                                                onChange={e => {
                                                    setClientSearch(e.target.value);
                                                    setShowClientDropdown(true);
                                                }}
                                                onFocus={() => setShowClientDropdown(true)}
                                            />
                                            {form.payerId && (
                                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                                </div>
                                            )}
                                        </div>

                                        {showClientDropdown && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                {companies
                                                    .filter(c =>
                                                        c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                                                        (c.cnpj && c.cnpj.includes(clientSearch))
                                                    )
                                                    .map(c => (
                                                        <div
                                                            key={c._id}
                                                            className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 flex justify-between items-center"
                                                            onClick={() => {
                                                                setForm({ ...form, payerId: c._id });
                                                                setClientSearch(c.name);
                                                                setShowClientDropdown(false);
                                                            }}
                                                        >
                                                            <div>
                                                                <div className="font-bold text-gray-800">{c.name}</div>
                                                                <div className="text-xs text-gray-500">{c.cnpj ? `CNPJ: ${c.cnpj}` : 'Sem CNPJ'}</div>
                                                            </div>
                                                            {form.payerId === c._id && <CheckCircle className="w-4 h-4 text-green-500" />}
                                                        </div>
                                                    ))}
                                                {companies.filter(c => c.name.toLowerCase().includes(clientSearch.toLowerCase()) || (c.cnpj && c.cnpj.includes(clientSearch))).length === 0 && (
                                                    <div className="p-3 text-gray-500 text-center text-sm">Nenhum cliente encontrado.</div>
                                                )}
                                            </div>
                                        )}
                                        {/* Overlay para fechar ao clicar fora - opcional mas útil */}
                                        {showClientDropdown && (
                                            <div className="fixed inset-0 z-0" onClick={() => setShowClientDropdown(false)} />
                                        )}
                                    </div>
                                    <div className="md:col-span-4">
                                        <label className="block text-sm font-bold text-gray-700 mb-1">Valor do Serviço (R$)</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-2.5 text-gray-500">R$</span>
                                            <input
                                                type="number" step="0.01"
                                                className="w-full pl-10 p-2.5 border rounded-lg font-bold text-gray-900 focus:ring-2 focus:ring-blue-500"
                                                value={form.amount}
                                                onChange={e => setForm({ ...form, amount: e.target.value })}
                                                placeholder="0,00"
                                            />
                                        </div>
                                    </div>

                                    {/* LINHA 3: Descrição */}
                                    <div className="md:col-span-12">
                                        <label className="block text-sm font-bold text-gray-700 mb-1">Descrição do Serviço</label>
                                        <textarea
                                            className="w-full p-3 border rounded-lg h-24 focus:ring-2 focus:ring-blue-500 text-sm"
                                            placeholder="Descreva os serviços prestados..."
                                            value={form.description}
                                            onChange={e => setForm({ ...form, description: e.target.value })}
                                        />
                                    </div>

                                    {/* LINHA 4: Códigos de Serviço */}
                                    <div className="md:col-span-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                        <label className="block text-xs font-bold text-gray-500 mb-1">Item LC 116/2003 *</label>
                                        <input type="text" className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-blue-500" placeholder="Ex: 01.07" value={form.itemLc116} onChange={e => setForm({ ...form, itemLc116: e.target.value })} />
                                    </div>
                                    <div className="md:col-span-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                        <label className="block text-xs font-bold text-gray-500 mb-1">CNAE *</label>
                                        <input type="text" className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-blue-500" placeholder="Ex: 6209100" value={form.cnae} onChange={e => setForm({ ...form, cnae: e.target.value })} />
                                    </div>
                                    <div className="md:col-span-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                        <label className="block text-xs font-bold text-gray-500 mb-1">Código NBS *</label>
                                        <input type="text" className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-blue-500" placeholder="Ex: 1.01..." value={form.codigoNbs} onChange={e => setForm({ ...form, codigoNbs: e.target.value })} />
                                    </div>

                                    {/* LINHA 5: Retenções e Outros */}
                                    <div className="md:col-span-12 grid grid-cols-1 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200 mt-2">
                                        <div className="flex items-center">
                                            <label className="flex items-center space-x-2 cursor-pointer select-none">
                                                <input type="checkbox" checked={form.issRetido} onChange={e => setForm({ ...form, issRetido: e.target.checked })} className="rounded w-5 h-5 text-blue-600 focus:ring-blue-500" />
                                                <span className="text-sm font-bold text-gray-700">ISS Retido na Fonte?</span>
                                            </label>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1">Responsável Retenção</label>
                                            <select className="w-full p-2 border rounded text-sm bg-white" value={form.responsavelRetencao} onChange={e => setForm({ ...form, responsavelRetencao: parseInt(e.target.value) })}>
                                                <option value={1}>1 - Tomador</option>
                                                <option value={2}>2 - Prestador</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1">Cód. Obra (Opcional)</label>
                                            <input className="w-full p-2 border rounded text-sm" value={form.obraCode} onChange={e => setForm({ ...form, obraCode: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1">ART (Opcional)</label>
                                            <input className="w-full p-2 border rounded text-sm" value={form.art} onChange={e => setForm({ ...form, art: e.target.value })} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* RODAPÉ FIXED */}
                            <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t shrink-0">
                                <div className="flex items-center space-x-2">
                                    <input type="checkbox" id="saveDef" checked={form.saveAsDefault} onChange={e => setForm({ ...form, saveAsDefault: e.target.checked })} className="rounded text-blue-600 focus:ring-blue-500" />
                                    <label htmlFor="saveDef" className="text-sm text-blue-600 font-bold cursor-pointer select-none">Salvar configurações como novo modelo</label>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => setModalOpen(false)} className="px-5 py-2.5 text-gray-600 hover:bg-gray-200 rounded-lg font-bold transition-colors">Cancelar</button>
                                    {form.saveAsDefault && (
                                        <button onClick={handleSaveAsModel} className="px-5 py-2.5 text-blue-600 hover:bg-blue-50 rounded-lg font-bold border border-blue-200 transition-colors">Salvar Modelo</button>
                                    )}
                                    <button onClick={handleEmit} disabled={emitting} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-bold flex items-center shadow-lg transition-all disabled:opacity-50 disabled:shadow-none hover:translate-y-[-1px]">
                                        {emitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}
                                        Emitir Nota Fiscal
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    );
};

export default NotasFiscaisScreen;
